import { ProductDetailsModalContextProvider } from '@app/components/products/ProductDetails/context/ProductDetailsModalContext';
import { ProductDetailsModal } from '@app/components/products/ProductDetails/ProductDetailsModal';
import { useSharedToken } from '@app/components/SharedTokenProvider';
import { Button } from '@app/components/various/Button';
import { Form } from '@app/components/various/Form';
import { addToast } from '@app/components/various/Toasts';
import {
  checkoutOrderRequest,
  getIsPrepaymentEnabled,
  getIsRequestingPayment,
  getIsSeller,
  getIsSubmittingOrder,
  getIsUpdatingShipping,
  requestOrderPayment,
  updateCheckoutForm,
} from '@app/ducks';
import { getTotalAmountInWholeOrder } from '@app/logic/Orders';
import { compiledPaths } from '@app/paths';
import { useAmplitude } from '@app/utils/analytics/useAmplitude';
import { createAbsoluteUrl } from '@app/utils/createAbsoluteUrl';
import { useTranslationsConfig, useUrlProductId } from '@app/utils/hooks';
import { useUrlVariantId } from '@app/utils/hooks/useUrlVariantId';
import { isDefined } from '@app/utils/is';
import { Checkout, CheckoutForm, Order } from '@typings';
import React from 'react';
import { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { CheckoutAdditional } from '../CheckoutAdditional/CheckoutAdditional';
import { CheckoutAlerts } from '../CheckoutAlerts';
import { CheckoutButton } from '../CheckoutButton';
import { CheckoutNoUnits } from '../CheckoutNoUnits';
import { CheckoutPoNumber } from '../CheckoutPoNumber';
import { CheckoutOrdersSplit } from '../CheckoutProducts';
import { CheckoutShippingAddress } from '../CheckoutShippingAddress/CheckoutShippingAddress';
import { CheckoutShippingPreferences } from '../CheckoutShippingPreferences';
import { CheckoutSummary } from '../CheckoutSummary';
import { CheckoutTermsAndConditions } from '../CheckoutTermsAndConditions';
import { convertCheckoutFormData } from '../convertCheckoutFormData';
import { SelectionSummary } from '../SelectionSummary/SelectionSummary';
import { useCheckoutForm } from '../useCheckoutForm';

interface Props {
  isLoading: boolean;
  isSaving: boolean;
  checkoutFields: Checkout.Requirements;
  orderDetails: Order.Single;
}

export const CheckoutContent = ({ isLoading, isSaving, checkoutFields, orderDetails }: Props) => {
  const { t } = useTranslation(['invoices', 'payments', 'validation', 'common', 'selections']);
  const dispatch = useDispatch();
  const isSubmittingOrder = useSelector(getIsSubmittingOrder);
  const isRequestingPayment = useSelector(getIsRequestingPayment);
  const isPrepaymentEnabled = useSelector(getIsPrepaymentEnabled);
  const isUpdatingShippingCost = useSelector(getIsUpdatingShipping);
  const isSeller = useSelector(getIsSeller);
  const { trackEvent } = useAmplitude();
  const productId = useUrlProductId();
  const variantId = useUrlVariantId();
  const sharedToken = useSharedToken();
  const noUnitsAdded = getTotalAmountInWholeOrder(orderDetails.order) === 0;
  const isSavingOrLoading = isLoading || isSaving;
  const { currentLanguageCode } = useTranslationsConfig();

  const formMethods = useCheckoutForm({ checkoutFields });
  const { getValues, formState } = formMethods;

  React.useEffect(() => {
    return () => {
      dispatch(updateCheckoutForm(getValues()));
    };
  }, [dispatch, getValues]);

  const handleCheckout = (data: CheckoutForm) => {
    const checkoutData = convertCheckoutFormData({ data, orderDetails });
    const orderId = checkoutData.order;

    if (isDefined(data.paymentMethod) && data.paymentMethod !== 'pay_later') {
      const searchParams = isDefined(sharedToken) ? { sharedToken } : undefined;
      const cancelUrl = createAbsoluteUrl(compiledPaths.ORDER_PAYMENT_FAILURE({ id: orderId }), searchParams);
      const successUrl = createAbsoluteUrl(compiledPaths.ORDER_PAYMENT_RESULT({ id: orderId }), searchParams);

      trackEvent({
        name: 'checkoutPage.button.confirmOrder.click',
        properties: { currentLanguage: currentLanguageCode, paymentMethod: data.paymentMethod },
      });

      dispatch(
        requestOrderPayment({
          checkoutData,
          orderId,
          payment: {
            cancelUrl,
            paymentMethod: data.paymentMethod,
            successUrl,
          },
        }),
      );

      return;
    }

    trackEvent({
      name: 'checkoutPage.button.confirmOrder.click',
      properties: { currentLanguage: currentLanguageCode, paymentMethod: 'withoutPayment' },
    });

    dispatch(checkoutOrderRequest(checkoutData));
  };

  const trackValidationErrorFields = React.useCallback(
    (errors: FieldErrors<CheckoutForm>) => {
      (Object.keys(errors) as (keyof CheckoutForm)[]).forEach(field => {
        if (isDefined(formState.defaultValues) && field in formState.defaultValues) {
          trackEvent({
            name: 'checkoutPage.formValidation.confirmOrder.failure',
            properties: { field },
          });
        }
      });
    },
    [formState.defaultValues, trackEvent],
  );

  const handleInvalidSubmit = (errors: FieldErrors<CheckoutForm>) => {
    trackValidationErrorFields(errors);
    addToast(t('validation:required_fields_hint'));
  };

  const handleEditSelection = () => {
    trackEvent({ name: 'checkoutPage.button.editSelection.click' });
  };

  return (
    <>
      <Form id="checkoutForm" formMethods={formMethods} onSubmit={handleCheckout} onInvalidSubmit={handleInvalidSubmit}>
        <div className="grid gap-x-10 lg:grid-cols-[1fr_26.5rem]">
          <div>
            <div className="mb-8 border-b border-neutral-90 pb-8">
              <CheckoutSummary orderDetails={orderDetails} isDisabled={noUnitsAdded} />
              <CheckoutAlerts orderDetails={orderDetails} />
            </div>

            {noUnitsAdded ?
              <CheckoutNoUnits orderId={orderDetails.order.order} />
            : <>
                <CheckoutOrdersSplit orderDetails={orderDetails} />
                <div className="space-y-8 pt-8">
                  <CheckoutPoNumber isLoading={isSavingOrLoading} checkoutFields={checkoutFields} />
                  <CheckoutShippingAddress
                    shippingAddresses={orderDetails?.account?.shippingAddresses}
                    isLoading={isSavingOrLoading}
                    checkoutFields={checkoutFields}
                  />
                  <CheckoutShippingPreferences isLoading={isSavingOrLoading} orderDetails={orderDetails} checkoutFields={checkoutFields} />
                  <CheckoutAdditional isLoading={isSavingOrLoading} checkoutFields={checkoutFields} />
                  {!isSeller && <CheckoutTermsAndConditions isDisabled={isSavingOrLoading} />}
                </div>
              </>
            }
            <div className="col-span-full my-8 block h-[1px] bg-neutral-90 lg:hidden" />
          </div>

          <div>
            <SelectionSummary
              handleEditSelection={handleEditSelection}
              orderDetails={orderDetails}
              isUpdatingShippingCost={isUpdatingShippingCost}
              isPrepaymentEnabled={isPrepaymentEnabled}
              isSavingOrLoading={isSavingOrLoading}
              hasUnitsAdded={!noUnitsAdded}
              isSubmittingOrder={isSubmittingOrder}
              isRequestingPayment={isRequestingPayment}
            />
          </div>

          <ProductDetailsModalContextProvider productId={productId} variantId={variantId}>
            <ProductDetailsModal modalLocation="checkout" />
          </ProductDetailsModalContextProvider>
        </div>
        <div className="sticky -bottom-16 left-0 right-0 z-topBar flex flex-col gap-4 rounded-ee-regular rounded-es-regular bg-white p-6 pt-4 lg:hidden">
          <CheckoutButton
            orderDetails={orderDetails}
            isDisabled={isSavingOrLoading || noUnitsAdded || isUpdatingShippingCost}
            isLoading={isSubmittingOrder || isRequestingPayment}
          />
          <Button
            as={Link}
            to={compiledPaths.SELECTION_DETAILS({ id: orderDetails.order.order })}
            isFluid
            onClick={handleEditSelection}
            variant="bordered"
            color="secondary"
            type="button"
            dataTestId="selectionStep"
          >
            {t('selections:edit_selection')}
          </Button>
        </div>
      </Form>
    </>
  );
};
